import { Navigate, Route, Routes } from 'react-router-dom'

import Login from './pages/LoginPage'
import Signup from './pages/SignupPage'
import Verify from './pages/VerifyEmailPage'
import Forgot from './pages/ForgotPasswordPage'
import Reset from './pages/ResetPasswordPage'
import Onboard from './pages/BusinessOnboardingPage'
import Select from './pages/WorkspaceSelectionPage'

import { PlatformAdminRoute, ProtectedRoute } from './components/Auth'
import PlatformAdminPage from './pages/PlatformAdminPage'
import Shell from './components/AppShell'

import Dashboard from './pages/DashboardPage'
import Products from './pages/ProductsPage'
import ProductNew from './pages/ProductCreatePage'
import Product from './pages/ProductDetailsPage'
import BarcodeManagementPage from './pages/BarcodeManagementPage'
import ProductHistoryPage from './pages/ProductHistoryPage'
import ExpiringProductsPage from './pages/ExpiringProductsPage'
import Ref from './pages/ReferenceDataPage'
import Inventory from './pages/InventoryActionPage'
import POS from './pages/PosPage'
import Resource, { type ResourceConfig } from './pages/ResourcePage'
import Detail from './pages/TransactionDetailPage'
import Create from './pages/TransactionCreatePage'
import Contact from './pages/ContactDetailPage'
import Financial from './pages/FinancialDetailPage'
import Categories from './pages/FinancialCategoriesPage'
import Staff from './pages/StaffPage'
import Payroll from './pages/PayrollPage'
import Delivery from './pages/DeliveryPage'
import Loyalty from './pages/LoyaltyPage'
import Settings from './pages/SettingsPage'
import BackendPendingPage from './pages/BackendPendingPage'
import AccountUtilityPage from './pages/AccountUtilityPage'
import NotificationsPage from './pages/NotificationsPage'
import AuditLogPage from './pages/AuditLogPage'
import DayClose from './pages/DayClosePage'
import ReportsPage from './pages/ReportsPage'
import PayablesPage from './pages/PayablesPage'
import GlobalSearchPage from './pages/GlobalSearchPage'
import ReceivablesPage from './pages/ReceivablesPage'

const x = (
  title: string,
  endpoint: string,
  detailPath?: string,
  categoryPath?: string,
): ResourceConfig => ({
  title,
  description: `Manage ${title.toLowerCase()} in this workspace.`,
  endpoint,
  fields: [],
  statusKey: 'status',
  detailPath,
  categoryPath,
  categoryEndpoint: undefined as string | undefined,
})

const contacts = (title: string, endpoint: string) => ({
  ...x(title, endpoint, endpoint),
  fields: [
    { key: 'name', label: 'Name', required: true },
    { key: 'phone', label: 'Phone' },
    { key: 'email', label: 'Email' },
  ],
})

const customers = contacts('Customers', '/customers')
const suppliers = contacts('Suppliers', '/suppliers')
const sales = x('Sales', '/sales', '/sales')
const purchases = x('Purchases', '/purchases', '/purchases')
const invoices = x('Invoices', '/invoices', '/invoices')
const quotes = x('Quotes', '/quotes', '/quotes')

const expenses = x(
  'Expenses',
  '/expenses',
  '/expenses',
  '/expenses/categories',
)

expenses.categoryEndpoint = '/expense-categories'
expenses.fields = [
  { key: 'description', label: 'Description', required: true },
  { key: 'amount', label: 'Amount', type: 'number', required: true },
  { key: 'expense_date', label: 'Expense date', type: 'date', required: true },
  { key: 'payment_method', label: 'Payment method', type: 'select', options: ['CASH', 'POS', 'TRANSFER', 'OTHER'], required: true },
  { key: 'reference', label: 'Reference' },
  { key: 'notes', label: 'Notes', type: 'textarea' },
]

const income = x(
  'Other Income',
  '/other-income',
  '/other-income',
  '/other-income/categories',
)

income.categoryEndpoint = '/other-income-categories'
income.fields = [
  { key: 'description', label: 'Description', required: true },
  { key: 'amount', label: 'Amount', type: 'number', required: true },
  { key: 'income_date', label: 'Income date', type: 'date', required: true },
  { key: 'reference', label: 'Reference' },
  { key: 'notes', label: 'Notes', type: 'textarea' },
]

function Pending({
  feature,
  description,
}: {
  feature: string
  description: string
}) {
  return (
    <BackendPendingPage
      feature={feature}
      description={description}
    />
  )
}

export default function App() {
  return (
    <Routes>
      {/* Public / authentication */}
      <Route
        path="/"
        element={<Navigate to="/dashboard" replace />}
      />

      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/verify-email" element={<Verify />} />
      <Route path="/forgot-password" element={<Forgot />} />
      <Route path="/reset-password" element={<Reset />} />

      <Route path="/onboarding" element={<Onboard />} />
      <Route path="/businesses/new" element={<Onboard />} />
      <Route path="/select-workspace" element={<Select />} />
      <Route element={<PlatformAdminRoute />}><Route path="/admin" element={<PlatformAdminPage />} /></Route>

      {/* Authenticated AAD application */}
      <Route element={<ProtectedRoute />}>
        <Route element={<Shell />}>
          {/* Main */}
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/pos" element={<POS />} />

          {/* Sales */}
          <Route
            path="/sales"
            element={<Resource config={sales} />}
          />
          <Route
            path="/sales/:saleId"
            element={<Detail kind="sales" />}
          />

          {/* Products */}
          <Route path="/products" element={<Products />} />
          <Route path="/products/new" element={<ProductNew />} />
          <Route
            path="/products/:productId"
            element={<Product />}
          />

          <Route path="/categories" element={<Ref />} />
          <Route path="/units" element={<Ref />} />

          {/* Product tools */}
          <Route path="/products/barcodes" element={<BarcodeManagementPage />} />

          <Route path="/products/history" element={<ProductHistoryPage />} />

          <Route path="/products/expiring" element={<ExpiringProductsPage />} />

          <Route
            path="/products/settings"
            element={
              <Pending
                feature="Product Settings"
                description="Configure product-related options, units, categories and product behaviour."
              />
            }
          />

          {/* Inventory */}
          <Route path="/inventory" element={<Inventory />} />

          <Route
            path="/inventory/adjust"
            element={<Inventory />}
          />

          <Route
            path="/inventory/transfer"
            element={<Inventory />}
          />

          <Route path="/inventory/low-stock" element={<Inventory />} />

          <Route path="/inventory/history" element={<Inventory />} />

          {/* Customers */}
          <Route
            path="/customers"
            element={<Resource config={customers} />}
          />

          <Route
            path="/customers/:customerId"
            element={<Contact kind="customers" />}
          />

          {/* Debtors / Receivables */}
          <Route
            path="/receivables"
            element={<ReceivablesPage mode="list" />}
          />
          <Route
            path="/receivables/reminders"
            element={<ReceivablesPage mode="reminders" />}
          />
          <Route
            path="/receivables/statements"
            element={<ReceivablesPage mode="statements" />}
          />
          <Route
            path="/receivables/:receivableId"
            element={<ReceivablesPage mode="detail" />}
          />

          {/* Loyalty */}
          <Route path="/loyalty" element={<Loyalty />} />

          {/* Supplier Payables */}
          <Route
            path="/payables"
            element={<PayablesPage />}
          />

          {/* Suppliers */}
          <Route
            path="/suppliers"
            element={<Resource config={suppliers} />}
          />

          <Route
            path="/suppliers/:supplierId"
            element={<Contact kind="suppliers" />}
          />

          {/* Purchases */}
          <Route
            path="/purchases"
            element={<Resource config={purchases} />}
          />

          <Route
            path="/purchases/new"
            element={<Create kind="purchases" />}
          />

          <Route
            path="/purchases/:purchaseId"
            element={<Detail kind="purchases" />}
          />

          {/* Invoices */}
          <Route
            path="/invoices"
            element={<Resource config={invoices} />}
          />

          <Route
            path="/invoices/new"
            element={<Create kind="invoices" />}
          />

          <Route
            path="/invoices/:invoiceId"
            element={<Detail kind="invoices" />}
          />

          {/* Quotes */}
          <Route
            path="/quotes"
            element={<Resource config={quotes} />}
          />

          <Route
            path="/quotes/new"
            element={<Create kind="quotes" />}
          />

          <Route
            path="/quotes/:quoteId"
            element={<Detail kind="quotes" />}
          />

          {/* Expenses */}
          <Route
            path="/expenses"
            element={<Resource config={expenses} />}
          />

          <Route
            path="/expenses/categories"
            element={<Categories kind="expense" />}
          />

          <Route
            path="/expenses/:expenseId"
            element={<Financial kind="expenses" />}
          />

          {/* Other Income */}
          <Route
            path="/other-income"
            element={<Resource config={income} />}
          />

          <Route
            path="/other-income/categories"
            element={<Categories kind="other-income" />}
          />

          <Route
            path="/other-income/:incomeId"
            element={<Financial kind="other-income" />}
          />

          {/* Day Close */}
          <Route path="/day-close" element={<DayClose />} />

          {/* Reports */}
          <Route path="/reports" element={<ReportsPage />} />
          <Route path="/reports/summary" element={<ReportsPage kind="summary" />} />
          <Route path="/reports/profit-loss" element={<ReportsPage kind="profit-loss" />} />
          <Route path="/reports/sales" element={<ReportsPage kind="sales" />} />
          <Route path="/reports/sales-products" element={<ReportsPage kind="sales-products" />} />
          <Route path="/reports/sales-staff" element={<ReportsPage kind="sales-staff" />} />
          <Route path="/reports/sales-branches" element={<ReportsPage kind="sales-branches" />} />
          <Route path="/reports/purchases" element={<ReportsPage kind="purchases" />} />
          <Route path="/reports/receivables" element={<ReportsPage kind="receivables" />} />
          <Route path="/reports/payables" element={<ReportsPage kind="payables" />} />
          <Route path="/reports/expenses" element={<ReportsPage kind="expenses" />} />
          <Route path="/reports/other-income" element={<ReportsPage kind="other-income" />} />
          <Route path="/reports/payroll" element={<ReportsPage kind="payroll" />} />
          <Route path="/reports/inventory-value" element={<ReportsPage kind="inventory-value" />} />
          <Route path="/reports/stock-history" element={<ReportsPage kind="stock-history" />} />
          {/* Staff */}
          <Route path="/staff" element={<Staff />} />
          <Route path="/staff/:id" element={<Staff />} />

          {/* Payroll */}
          <Route path="/payroll" element={<Payroll />} />
          <Route path="/payroll/:id" element={<Payroll />} />

          {/* Delivery */}
          <Route path="/deliveries" element={<Delivery />} />
          <Route path="/deliveries/:id" element={<Delivery />} />

          {/* Restaurant */}
          <Route
            path="/restaurant/tables"
            element={
              <Pending
                feature="Restaurant Tables"
                description="Manage restaurant tables, seating and floor-based order workflows."
              />
            }
          />

          <Route
            path="/restaurant/orders"
            element={
              <Pending
                feature="Table Orders"
                description="Manage restaurant orders associated with tables and floor service."
              />
            }
          />

          <Route
            path="/restaurant/settings"
            element={
              <Pending
                feature="Restaurant Settings"
                description="Configure optional restaurant table and ordering functionality."
              />
            }
          />

          {/* Online Store */}
          <Route
            path="/online-store"
            element={
              <Pending
                feature="Online Store"
                description="Manage products made available through the business online store."
              />
            }
          />

          <Route
            path="/online-store/orders"
            element={
              <Pending
                feature="Online Store Orders"
                description="Manage customer orders placed through the business online store."
              />
            }
          />

          <Route
            path="/online-store/settings"
            element={
              <Pending
                feature="Online Store Settings"
                description="Configure online store behaviour, order settings and publishing preferences."
              />
            }
          />

          {/* Marketplace */}
          <Route path="/marketplace" element={<Pending feature="Marketplace Listings" description="Marketplace listings are awaiting a backend connection." />} />
          <Route path="/marketplace/new" element={<Pending feature="Create Marketplace Listing" description="Marketplace listing creation is awaiting a backend connection." />} />
          <Route path="/marketplace/subscription" element={<Pending feature="Marketplace Subscription" description="Marketplace subscriptions are awaiting a backend connection." />} />

          {/* Market Intelligence */}
          <Route
            path="/market-intelligence"
            element={
              <Pending
                feature="Market Intelligence"
                description="Explore anonymized demand trends, price ranges, regional activity, seasonal trends and fast-moving categories."
              />
            }
          />

          {/* Agents / Referral */}
          <Route path="/agents" element={<Pending feature="Agents & Referral" description="Agent and referral overview is awaiting a backend connection." />} />
          <Route path="/agents/referrals" element={<Pending feature="Referrals" description="Referral tracking is awaiting a backend connection." />} />
          <Route path="/agents/commissions" element={<Pending feature="Agent Commissions" description="Commission tracking is awaiting a backend connection." />} />
          <Route path="/agents/payouts" element={<Pending feature="Agent Payout History" description="Agent payout history is awaiting a backend connection." />} />

          {/* Global Search */}
          <Route
            path="/global-search"
            element={<GlobalSearchPage />}
          />

          {/* Import */}
          <Route
            path="/import"
            element={
              <Pending
                feature="Import Previous Data"
                description="Import business records from Excel, CSV and supported previous systems."
              />
            }
          />

          {/* AI Assistant */}
          <Route
            path="/assistant"
            element={
              <Pending
                feature="AAD AI Business Assistant"
                description="Receive business summaries and assistance using your real sales, stock, debt, payroll and operational data."
              />
            }
          />

          {/* Notifications */}
          <Route path="/notifications" element={<NotificationsPage />} />

          {/* Audit */}
          <Route path="/audit" element={<AuditLogPage />} />

          {/* Subscription */}
          <Route path="/subscription" element={<AccountUtilityPage kind="subscription" />} />

          {/* Language */}
          <Route path="/language" element={<AccountUtilityPage kind="language" />} />

          {/* Install / PWA */}
          <Route path="/install" element={<AccountUtilityPage kind="install" />} />

          {/* Settings */}
          <Route path="/settings" element={<Settings />} />
        </Route>
      </Route>

      <Route
        path="*"
        element={<Navigate to="/dashboard" replace />}
      />
    </Routes>
  )
}
